import { useState, type ChangeEvent, type FormEvent } from 'react';
import { callable } from '@/lib/firebase';
import { normalizePhoneInput } from '@/lib/phone';
import { messageForAuthError } from '@/lib/authErrors';
import { strings } from '@/strings';

type SubmitResult = { id: string; duplicate: boolean };

const submitNomination = callable<
  { candidateName: string; candidatePhone: string; why: string; currentRole: string; employer: string; city: string },
  SubmitResult
>('submitNomination');

const EMPTY = { name: '', phone: '', why: '', currentRole: '', employer: '', city: '' };

/**
 * The whole product depends on this form taking under three minutes. Three
 * fields are required and the rest are visibly optional; nothing here asks for
 * a CV, a title, or a relationship, because every extra required field is a
 * nomination that never arrives.
 */
export function NominatePage() {
  const [form, setForm] = useState(EMPTY);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canonical = normalizePhoneInput(form.phone);
  const ready = Boolean(canonical) && form.name.trim() !== '' && form.why.trim() !== '';

  function set(field: keyof typeof EMPTY) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!ready || busy || !canonical) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await submitNomination({
        candidateName: form.name.trim(),
        candidatePhone: canonical,
        why: form.why.trim(),
        currentRole: form.currentRole.trim(),
        employer: form.employer.trim(),
        city: form.city.trim(),
      }));
    } catch (err) {
      // The form is NOT cleared on failure: retyping a nomination someone
      // already thought about is how you lose it.
      setError(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <div className="flex max-w-md flex-col gap-6">
        <h1 className="text-xl font-bold text-primary">{strings.nominateTitle}</h1>
        <p className="text-success">{result.duplicate ? strings.nominateDuplicate : strings.nominateSent}</p>
        <button
          type="button"
          className="self-start min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => { setResult(null); setForm(EMPTY); }}
        >
          {strings.nominateAnother}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex max-w-md flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.nominateTitle}</h1>
      <p className="text-sm text-muted">{strings.nominateIntro}</p>

      <label className="flex flex-col gap-2 text-sm" htmlFor="name">
        {strings.nominateName}
        <input id="name" value={form.name} onChange={set('name')}
          className="rounded-md border border-border-subtle px-3 py-2" />
      </label>

      <label className="flex flex-col gap-2 text-sm" htmlFor="phone">
        {strings.signInPhoneLabel}
        <input id="phone" inputMode="tel" dir="ltr" value={form.phone} onChange={set('phone')}
          placeholder={strings.signInPhonePlaceholder}
          className="rounded-md border border-border-subtle px-3 py-2 text-right" />
      </label>

      <label className="flex flex-col gap-2 text-sm" htmlFor="why">
        {strings.nominateWhy}
        <textarea id="why" rows={3} value={form.why} onChange={set('why')}
          className="rounded-md border border-border-subtle px-3 py-2" />
      </label>

      <label className="flex flex-col gap-2 text-sm" htmlFor="role">
        {strings.nominateRole}
        <input id="role" value={form.currentRole} onChange={set('currentRole')}
          className="rounded-md border border-border-subtle px-3 py-2" />
      </label>

      <label className="flex flex-col gap-2 text-sm" htmlFor="employer">
        {strings.nominateEmployer}
        <input id="employer" value={form.employer} onChange={set('employer')}
          className="rounded-md border border-border-subtle px-3 py-2" />
      </label>

      <label className="flex flex-col gap-2 text-sm" htmlFor="city">
        {strings.nominateCity}
        <input id="city" value={form.city} onChange={set('city')}
          className="rounded-md border border-border-subtle px-3 py-2" />
      </label>

      <button type="submit" disabled={!ready || busy}
        className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
        {strings.nominateSubmit}
      </button>

      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
    </form>
  );
}
